// src/components/ScoringAnalyticsCharts.js
// Updated: 11/10/2025 11:05 PM EST
// --- FULL, REPLACEMENT FILE ---
// PackageBarChart: blue bars (mean)
// PackageBoxPlot: amber box+whiskers (spread) + per-reviewer colored dots + black median dot

import React from 'react';
import {
  Chart as ChartJS,
  // scales
  CategoryScale,
  LinearScale,
  // elements
  BarElement,
  PointElement,
  LineElement,
  // plugins
  Title,
  Tooltip,
  Legend,
  // controllers (explicit for tree-shaking)
  LineController,
  ScatterController,
  BarController, // not strictly required if chart type="bar" at root, but safe
} from 'chart.js';
import { Bar, Chart } from 'react-chartjs-2';

// Register Chart.js bits (include controllers for mixed 'scatter' datasets)
ChartJS.register(
  // scales
  CategoryScale,
  LinearScale,
  // elements
  BarElement,
  PointElement,
  LineElement,
  // controllers
  LineController,
  ScatterController,
  BarController,
  // plugins
  Title,
  Tooltip,
  Legend
);

/** ---------- Utility helpers ---------- */
function quantile(sorted, q) {
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

function summarize(values = []) {
  const nums = values.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (!nums.length) return { min: 0, q1: 0, median: 0, q3: 0, max: 0 };
  const sorted = [...nums].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const q1 = quantile(sorted, 0.25);
  const median = quantile(sorted, 0.5);
  const q3 = quantile(sorted, 0.75);
  return { min, q1, median, q3, max };
}

/** ---------- Custom plugin to draw boxplots (no external deps) ---------- */
const customBoxPlotPlugin = {
  id: 'customBoxPlot',
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const { ctx, scales } = chart;
    const { x: xScale, y: yScale } = scales;

    const cfg = {
      boxWidthRatio: pluginOptions?.boxWidthRatio ?? 0.5,
      capWidthRatio: pluginOptions?.capWidthRatio ?? 0.35,
      boxLineWidth: pluginOptions?.boxLineWidth ?? 2,
      whiskerLineWidth: pluginOptions?.whiskerLineWidth ?? 2,
      medianLineWidth: pluginOptions?.medianLineWidth ?? 2,
      boxFill: pluginOptions?.boxFill ?? 'rgba(234, 179, 8, 0.28)', // amber
      boxStroke: pluginOptions?.boxStroke ?? 'rgba(234, 179, 8, 1)',
      whiskerStroke: pluginOptions?.whiskerStroke ?? 'rgba(234, 179, 8, 1)',
      medianStroke: pluginOptions?.medianStroke ?? 'rgba(0, 0, 0, 0.9)',
      summaries: pluginOptions?.summaries ?? [],
    };

    const labels = chart.config.data.labels || [];
    const count = Math.min(labels.length, cfg.summaries.length);
    if (!count) return;

    ctx.save();

    for (let i = 0; i < count; i++) {
      const s = cfg.summaries[i];
      if (!s) continue;

      const centerX = xScale.getPixelForValue(i);
      const categoryWidth =
        (xScale.getPixelForValue(i + 0.5) - xScale.getPixelForValue(i - 0.5)) || 40;
      const boxWidth = Math.max(6, categoryWidth * cfg.boxWidthRatio);
      const halfBox = boxWidth / 2;

      const yMin = yScale.getPixelForValue(s.min);
      const yQ1 = yScale.getPixelForValue(s.q1);
      const yMed = yScale.getPixelForValue(s.median);
      const yQ3 = yScale.getPixelForValue(s.q3);
      const yMax = yScale.getPixelForValue(s.max);

      // Whiskers
      ctx.strokeStyle = cfg.whiskerStroke;
      ctx.lineWidth = cfg.whiskerLineWidth;
      ctx.beginPath();
      ctx.moveTo(centerX, yMin);
      ctx.lineTo(centerX, yQ1);
      ctx.moveTo(centerX, yQ3);
      ctx.lineTo(centerX, yMax);
      ctx.stroke();

      // Whisker caps
      const capHalf = (categoryWidth * cfg.capWidthRatio) / 2;
      ctx.beginPath();
      ctx.moveTo(centerX - capHalf, yMin);
      ctx.lineTo(centerX + capHalf, yMin);
      ctx.moveTo(centerX - capHalf, yMax);
      ctx.lineTo(centerX + capHalf, yMax);
      ctx.stroke();

      // Box (Q1..Q3)
      const boxTop = Math.min(yQ1, yQ3);
      const boxHeight = Math.abs(yQ3 - yQ1);
      ctx.fillStyle = cfg.boxFill;
      ctx.strokeStyle = cfg.boxStroke;
      ctx.lineWidth = cfg.boxLineWidth;
      ctx.beginPath();
      ctx.rect(centerX - halfBox, boxTop, boxWidth, boxHeight);
      ctx.fill();
      ctx.stroke();

      // Median line
      ctx.strokeStyle = cfg.medianStroke;
      ctx.lineWidth = cfg.medianLineWidth;
      ctx.beginPath();
      ctx.moveTo(centerX - halfBox, yMed);
      ctx.lineTo(centerX + halfBox, yMed);
      ctx.stroke();
    }

    ctx.restore();
  },
};

ChartJS.register(customBoxPlotPlugin);

/**
 * Bar chart for average weighted scores (simple mean per package)
 */
export const PackageBarChart = ({ data }) => {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Average (Mean) Weighted Score by Package',
        font: { size: 18 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)}`,
        },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Avg. Weighted Score' } },
      x: { ticks: { autoSkip: false } },
    },
  };

  const chartData = {
    labels: data.map((pkg) => pkg.name),
    datasets: [
      {
        label: 'Average Weighted Score',
        data: data.map((pkg) =>
          Number(pkg?.avgWeightedScore?.toFixed?.(2) ?? pkg?.avgWeightedScore ?? 0)
        ),
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="h-96">
      <Bar options={options} data={chartData} datasetIdKey="id" />
    </div>
  );
};

/**
 * Box plot with:
 * - Amber box & whiskers (spread)
 * - Per-reviewer colored dots (consistency by person)
 * - Black "Median" dot for each package
 */
export const PackageBoxPlot = ({ data }) => {
  const labels = data.map((pkg) => pkg.name);

  // valuesByPkg: per package array of numeric scores
  const valuesByPkg = data.map((pkg) =>
    (pkg.scoresByReviewer || []).map((s) => Number(s?.weighted ?? 0))
  );

  const summaries = valuesByPkg.map((vals) => summarize(vals));

  // Number of reviewers (max length across packages)
  const maxReviewers = Math.max(...valuesByPkg.map((vals) => vals.length), 0);

  // Distinct color palette (cycled)
  const palette = [
    '#2563eb', '#16a34a', '#dc2626', '#9333ea', '#eab308',
    '#0891b2', '#f97316', '#64748b', '#d946ef', '#84cc16',
  ];

  // Build one dataset per reviewer so the legend shows "Reviewer 1/2/3..."
  const reviewerDatasets = Array.from({ length: maxReviewers }, (_, reviewerIndex) => {
    const points = labels.map((lbl, pkgIdx) => {
      const vals = valuesByPkg[pkgIdx] || [];
      const y = vals[reviewerIndex];
      return y != null && Number.isFinite(y) ? { x: lbl, y } : null;
    }).filter(Boolean);

    return {
      type: 'scatter',
      label: `Reviewer ${reviewerIndex + 1}`,
      data: points,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: palette[reviewerIndex % palette.length],
      pointBorderColor: palette[reviewerIndex % palette.length],
      showLine: false,
      order: 1, // drawn above box, below median dot
    };
  }).filter((ds) => ds.data.length > 0);

  // Median points dataset (one black dot per package)
  const medianPoints = summaries.map((s, i) => ({ x: labels[i], y: s.median ?? 0 }));
  const medianDataset = {
    type: 'scatter',
    label: 'Median',
    data: medianPoints,
    pointRadius: 5,
    pointHoverRadius: 7,
    pointStyle: 'rectRot', // diamond shape
    pointBackgroundColor: 'rgba(0,0,0,0.95)',
    pointBorderColor: 'rgba(0,0,0,0.95)',
    showLine: false,
    order: 2, // on top
  };

  // Compute overall Y range
  const allVals = valuesByPkg.flat().filter(Number.isFinite);
  const yMin = allVals.length ? Math.min(...allVals) : 0;
  const yMax = allVals.length ? Math.max(...allVals) : 1;
  const pad = (yMax - yMin) * 0.05 || 1;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: true, position: 'top' },
      title: {
        display: true,
        text: 'Spread of Weighted Scores by Package (Reviewers + Median)',
        font: { size: 18 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (ctx.dataset.label === 'Median') {
              return `Median: ${Number(ctx.raw?.y).toFixed(2)}`;
            }
            if (ctx.dataset.type === 'scatter') {
              return `${ctx.dataset.label}: ${Number(ctx.raw?.y).toFixed(2)}`;
            }
            return ctx.formattedValue ?? '';
          },
          afterBody: (items) => {
            const pkgLabel = items?.[0]?.raw?.x ?? '';
            const pkgIndex = labels.indexOf(pkgLabel);
            const s = summaries[pkgIndex];
            if (!s) return '';
            return `min ${s.min.toFixed(2)}, q1 ${s.q1.toFixed(2)}, `
              + `med ${s.median.toFixed(2)}, q3 ${s.q3.toFixed(2)}, max ${s.max.toFixed(2)}`;
          },
        },
      },
      // Box/whiskers renderer
      customBoxPlot: {
        boxWidthRatio: 0.5,
        capWidthRatio: 0.35,
        summaries,
      },
    },
    scales: {
      y: {
        type: 'linear',
        min: yMin - pad,
        max: yMax + pad,
        title: { display: true, text: 'Weighted Score' },
      },
      x: { ticks: { autoSkip: false } },
    },
  };

  const chartData = { labels, datasets: [...reviewerDatasets, medianDataset] };

  return (
    <div className="h-96">
      <Chart
        key={JSON.stringify({ labels, yMin, yMax })}
        type="bar" // category scale convenience; visuals come from plugin + scatter
        options={options}
        data={chartData}
        redraw
        datasetIdKey="id"
      />
    </div>
  );
};
